import React from "react";
import { Play, Pause, Heart, Share2, Radio } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

export default function StationCard({ station }) {
  const { current, playing, playStation, togglePlay, isFavorite, toggleFavorite, shareStation } = usePlayer();
  const isCurrent = current?.stationuuid === station.stationuuid;
  const isPlaying = isCurrent && playing;
  const fav = isFavorite(station.stationuuid);

  const tags = (station.tags || "").split(",").filter(Boolean).slice(0, 3);

  const onPlay = (e) => {
    e.stopPropagation();
    if (isCurrent) togglePlay();
    else playStation(station);
  };

  return (
    <div
      data-testid={`station-card-${station.stationuuid}`}
      onClick={onPlay}
      className={`station-card ${isCurrent ? "playing" : ""} group cursor-pointer relative border border-[var(--voix-border)] bg-[#080808] p-3 flex gap-3 items-start`}
    >
      {/* Favicon / placeholder */}
      <div className="w-12 h-12 shrink-0 bg-black border border-[var(--voix-border)] flex items-center justify-center overflow-hidden">
        {station.favicon ? (
          <img
            src={station.favicon}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <Radio size={20} className="text-slate-600" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-heading font-bold text-sm truncate text-white">{station.name}</div>
            <div className="font-mono-tech text-[9px] uppercase tracking-widest text-slate-500 mt-0.5 truncate">
              {station.country || "—"}{station.bitrate ? ` · ${station.bitrate}kbps` : ""}
            </div>
          </div>
          {isCurrent && (
            <span className="onair-dot w-2 h-2 rounded-full bg-green-500 shrink-0 mt-1.5" />
          )}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {tags.map((t) => (
              <span key={t} className="text-[9px] font-mono-tech uppercase tracking-wide text-slate-400 border border-[var(--voix-border)] px-1.5 py-0.5">
                {t.trim()}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="absolute right-2 top-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
        <button
          data-testid={`fav-btn-${station.stationuuid}`}
          onClick={(e) => { e.stopPropagation(); toggleFavorite(station); }}
          className={`w-7 h-7 flex items-center justify-center border border-[var(--voix-border)] bg-black/60 hover:border-pink-500 transition ${fav ? "text-pink-400" : "text-slate-500"}`}
          title="Favorite"
        >
          <Heart size={12} fill={fav ? "currentColor" : "none"} />
        </button>
        <button
          data-testid={`share-btn-${station.stationuuid}`}
          onClick={(e) => { e.stopPropagation(); shareStation(station); }}
          className="w-7 h-7 flex items-center justify-center border border-[var(--voix-border)] bg-black/60 text-slate-500 hover:border-cyan-500 hover:text-cyan-300 transition"
          title="Share"
        >
          <Share2 size={12} />
        </button>
      </div>

      {/* Play indicator button — visible bottom-right */}
      <button
        data-testid={`play-btn-${station.stationuuid}`}
        onClick={onPlay}
        className="absolute right-2 bottom-2 w-8 h-8 flex items-center justify-center bg-cyan-500 text-black hover:bg-cyan-400 transition opacity-0 group-hover:opacity-100"
      >
        {isPlaying ? <Pause size={14} /> : <Play size={14} />}
      </button>
    </div>
  );
}
